import { makeAutoObservable, runInAction } from "mobx";
import { graphqlRequest } from "@/graphql/client";
import { batchGetProperties } from "@/graphql/queries";
import { logInfo, logError } from "@/utils/errorLogger";
import {
  extractBodyString,
  fetchPropertiesFromApi,
  isSuccessStatusCode,
  normalizeProperty,
} from "@/store/propertyCore/propertyApi";
import { cachedPropertyIdMatchesKey } from "@/store/propertyCore/propertyCacheUtils";
import { PROCESS_TYPE } from "@/store/subscriptionCore/processConstants";
import { runExitStrategyProcess } from "@/store/subscriptionCore/processHandlers/exitStrategyHandler";
import { IS_DEV } from "@/utils/env";
import { exitStrategyCardTitle, toStrategyKey } from "@/pages/ExitStrategyAnalysis/constants";

/**
 * @param {Object|null|undefined} strategy
 * @returns {Object|null|undefined}
 */
function withExitStrategyDisplayTitle(strategy) {
  if (!strategy || typeof strategy !== "object") return strategy;
  return {
    ...strategy,
    display_name: exitStrategyCardTitle(strategy.strategy_name, strategy.display_name),
  };
}

export const EXIT_STRATEGIES = {
  FIX_AND_FLIP: "fix_and_flip",
  BRRRR: "brrrr",
  WHOLESALE: "wholesale",
  SHORT_TERM_RENTAL: "short_term_rental",
  HOUSE_HACKING: "house_hacking",
  COMMERCIAL_MIXED_USE: "commercial_mixed_use",
  BUY_AND_HOLD: "buy_hold_long_term_rental",
  PADSPLIT: "padsplit",
  ADU_DADU: "adu_dadu",
  ADU_FIX_AND_FLIP: "adu_fix_and_flip",
  LIVLYTE: "livlyte",
  SPLASH_AND_DASH: "splash_and_dash",
};

const EXIT_STRATEGY_ALIASES = {
  buy_and_hold: EXIT_STRATEGIES.BUY_AND_HOLD,
  creative_finance: EXIT_STRATEGIES.COMMERCIAL_MIXED_USE,
  livelyte: EXIT_STRATEGIES.LIVLYTE,
  adu_fix_and_flip: EXIT_STRATEGIES.ADU_DADU,
};

/**
 * Resolves strategy name to its backend-canonical form via aliases.
 * Does not reject unknown names — the backend validates.
 *
 * @param {string} strategyName
 * @returns {string|null} null only when input is empty/nullish
 */
function normalizeStrategyNameForRetry(strategyName) {
  const raw = String(strategyName ?? "").trim();
  if (!raw) return null;
  return EXIT_STRATEGY_ALIASES[raw] ?? raw;
}

/**
 * Backend markdown often ends with a line like `PRICE: $850,000` even when the structured
 * `price` field is missing or stripped during persistence. Uses last match so earlier
 * mentions do not override the final verdict line.
 *
 * @param {string} markdown
 * @returns {string|null}
 */
function extractDealPriceFromMarkdown(markdown) {
  if (typeof markdown !== "string") return null;
  const normalized = markdown.replace(/\\n/g, "\n").replace(/\\r/g, "\r");
  const re = /PRICE:\s*(\$[\d,]+(?:\.\d+)?)\b/gi;
  let m;
  let last = null;
  while ((m = re.exec(normalized)) !== null) {
    last = m[1].trim();
  }
  return last;
}

/**
 * DynamoDB AttributeValue or AppSync-tagged leaf → plain string/number/boolean/null.
 *
 * @param {unknown} value
 * @param {number} depth
 * @returns {string|number|boolean|null|undefined} undefined = not a simple leaf, may be Map/List
 */
function unwrapDynamoLeaf(value, depth = 0) {
  if (value == null || depth > 8) return null;
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return value;
  }
  if (typeof value !== "object") return null;
  if ("S" in value && typeof value.S === "string") return value.S;
  if ("N" in value && value.N != null) return value.N;
  if ("BOOL" in value) return value.BOOL;
  if ("NULL" in value && value.NULL === true) return null;
  return undefined;
}

/**
 * Pulls a human-readable price from nested Dynamo AttributeValue shapes (M/L).
 *
 * @param {unknown} value
 * @param {number} depth
 * @returns {string|number|null}
 */
function coercePriceFromDynamoValue(value, depth = 0) {
  if (value == null || depth > 10) return null;

  const leaf = unwrapDynamoLeaf(value, depth);
  if (leaf !== undefined) {
    if (typeof leaf === "string") {
      const t = leaf.trim();
      return t === "" ? null : t;
    }
    if (typeof leaf === "number") return leaf;
    return null;
  }

  if (typeof value !== "object" || value === null) return null;

  if ("M" in value && value.M && typeof value.M === "object") {
    const preferredKeys = ["price", "true_deal_price", "deal_price", "TrueDealPrice"];
    for (const k of preferredKeys) {
      if (k in value.M) {
        const inner = coercePriceFromDynamoValue(value.M[k], depth + 1);
        if (inner != null && inner !== "") return inner;
      }
    }
    for (const v of Object.values(value.M)) {
      const inner = coercePriceFromDynamoValue(v, depth + 1);
      if (inner != null && inner !== "") return inner;
    }
  }

  if ("L" in value && Array.isArray(value.L)) {
    for (const item of value.L) {
      const inner = coercePriceFromDynamoValue(item, depth + 1);
      if (inner != null && inner !== "") return inner;
    }
  }

  return null;
}

/**
 * Generic scalar unwrap for string fields (e.g. markdown in `{ S: "..." }`).
 *
 * @param {unknown} value
 * @returns {string|null}
 */
function unwrapStringAttribute(value) {
  const leaf = unwrapDynamoLeaf(value, 0);
  if (typeof leaf === "string") return leaf;
  if (typeof value === "string") return value;
  return null;
}

/**
 * Single strategy row may be an object, a JSON string (Dynamo map), or wrapped scalars.
 *
 * @param {unknown} row
 * @returns {Object|null}
 */
function coerceExitStrategyReportRow(row) {
  if (row == null) return null;
  if (typeof row === "string") {
    try {
      const parsed = JSON.parse(row);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
  if (typeof row === "object" && !Array.isArray(row)) return row;
  return null;
}

/**
 * Resolves displayable deal price from structured fields or markdown fallback.
 *
 * @param {Object} row
 * @param {string} markdown
 * @returns {string|number|null}
 */
function pickDealPriceForStrategy(row, markdown) {
  const candidates = [
    coercePriceFromDynamoValue(row.true_deal_price),
    coercePriceFromDynamoValue(row.price),
    coercePriceFromDynamoValue(row.deal_price),
    unwrapDynamoLeaf(row.price),
    unwrapDynamoLeaf(row.true_deal_price),
  ];
  for (const c of candidates) {
    if (c == null || c === "") continue;
    if (typeof c === "number") return c;
    if (typeof c === "string") {
      const t = c.trim();
      if (t !== "") return t;
    }
  }
  return extractDealPriceFromMarkdown(markdown);
}

/**
 * Resolves break-even purchase price for strategy cards.
 *
 * During migration, `true_deal_price`/`price` remain fallback sources until backend
 * removes `true_deal_price`.
 *
 * @param {Object} row
 * @param {string} markdown
 * @returns {string|number|null}
 */
function pickBreakEvenPriceForStrategy(row, markdown) {
  const candidates = [
    coercePriceFromDynamoValue(row.break_even_price),
    coercePriceFromDynamoValue(row.true_deal_price),
    coercePriceFromDynamoValue(row.price),
    coercePriceFromDynamoValue(row.deal_price),
    unwrapDynamoLeaf(row.break_even_price),
    unwrapDynamoLeaf(row.true_deal_price),
    unwrapDynamoLeaf(row.price),
  ];
  for (const c of candidates) {
    if (c == null || c === "") continue;
    if (typeof c === "number") return c;
    if (typeof c === "string") {
      const t = c.trim();
      if (t !== "") return t;
    }
  }
  return extractDealPriceFromMarkdown(markdown);
}

/**
 * @param {unknown} raw
 * @returns {boolean|null}
 */
function coerceBooleanField(raw) {
  const leaf = unwrapDynamoLeaf(raw, 0);
  if (typeof leaf === "boolean") return leaf;
  if (typeof leaf === "number") return leaf === 1 ? true : leaf === 0 ? false : null;
  if (typeof leaf === "string") {
    const v = leaf.trim().toLowerCase();
    if (v === "true" || v === "1") return true;
    if (v === "false" || v === "0") return false;
  }
  if (typeof raw === "boolean") return raw;
  return null;
}

/**
 * @param {unknown} raw
 * @returns {Array<Object>|null}
 */
function coerceProfitCurve(raw) {
  if (raw == null) return null;
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
  if (typeof raw === "object" && raw !== null && Array.isArray(raw.L)) {
    return raw.L.map((item) => {
      if (item && typeof item === "object" && item.M && typeof item.M === "object") {
        const out = {};
        for (const [k, v] of Object.entries(item.M)) {
          const leaf = unwrapDynamoLeaf(v, 0);
          out[k] = leaf !== undefined ? leaf : v;
        }
        return out;
      }
      const leaf = unwrapDynamoLeaf(item, 0);
      return leaf !== undefined ? leaf : item;
    });
  }
  return null;
}

/**
 * @param {unknown} error - Dynamo may use { NULL: true } (truthy object; must not count as error)
 * @returns {boolean}
 */
function rowHasMeaningfulError(error) {
  if (error == null) return false;
  const leaf = unwrapDynamoLeaf(error, 0);
  if (leaf === null || leaf === undefined) return false;
  if (typeof leaf === "string") return leaf.trim().length > 0;
  if (typeof leaf === "boolean") return leaf === true;
  if (typeof error === "string") return error.trim().length > 0;
  if (typeof error === "object" && "NULL" in error && error.NULL === true) return false;
  return false;
}

/** Row `status` values that mean the report is safe to hydrate (AppSync uses `done`; Dynamo may use `success`). */
const HYDRATE_OK_REPORT_STATUSES = new Set(["success", "done", "completed"]);

/**
 * @param {unknown} status
 * @returns {boolean} true when row should be skipped as non-success
 */
function rowStatusBlocksHydrate(status) {
  const leaf = unwrapDynamoLeaf(status, 0);
  if (leaf === null || leaf === undefined) return false;
  if (typeof leaf === "string") {
    const normalized = leaf.trim().toLowerCase();
    if (normalized === "") return false;
    return !HYDRATE_OK_REPORT_STATUSES.has(normalized);
  }
  return false;
}

/**
 * Normalizes string maps stored on `additional_attributes` (e.g. S3 keys, PDF URLs).
 *
 * @param {unknown} raw
 * @returns {Object<string, string>}
 */
function normalizeStrategyStringMap(raw) {
  let source = raw;
  if (typeof source === "string") {
    try {
      source = JSON.parse(source);
    } catch {
      return {};
    }
  }
  if (!source || typeof source !== "object" || Array.isArray(source)) return {};

  const out = {};
  for (const [key, value] of Object.entries(source)) {
    const mapKey = String(key ?? "").trim();
    if (!mapKey) continue;
    const leaf = unwrapDynamoLeaf(value, 0);
    const rawValue =
      typeof leaf === "string"
        ? leaf
        : typeof value === "string"
          ? value
          : typeof value === "object" && value !== null && "S" in value
            ? unwrapStringAttribute(value)
            : null;
    const normalizedValue = typeof rawValue === "string" ? rawValue.trim() : "";
    if (!normalizedValue) continue;
    out[mapKey] = normalizedValue;
  }
  return out;
}

/**
 * @param {Object} rowObj
 * @returns {string|null} First HTTP(S) URL found on known backend fields
 */
function pickExitStrategyReportPdfUrl(rowObj) {
  if (!rowObj || typeof rowObj !== "object") return null;
  const keys = ["report_pdf_url", "pdf_url", "reportPdfUrl", "pdfUrl"];
  for (const key of keys) {
    const raw = rowObj[key];
    const leaf = unwrapDynamoLeaf(raw, 0);
    const s =
      typeof leaf === "string" ? leaf.trim() : typeof raw === "string" ? String(raw).trim() : "";
    if (s && /^https?:\/\//i.test(s)) return s;
  }
  return null;
}

/**
 * @param {Object} rowObj
 * @returns {string|null} First non-empty S3 key found on known backend fields
 */
function pickExitStrategyReportPdfS3Key(rowObj) {
  if (!rowObj || typeof rowObj !== "object") return null;
  const keys = ["pdf_s3_key", "pdfS3Key"];
  for (const key of keys) {
    const raw = rowObj[key];
    const leaf = unwrapDynamoLeaf(raw, 0);
    const s =
      typeof leaf === "string" ? leaf.trim() : typeof raw === "string" ? String(raw).trim() : "";
    if (s) return s;
  }
  return null;
}

/**
 * Normalizes `additional_attributes.exit_strategy_reports` (object keyed by strategy id)
 * into the strategy array shape used by ExitStrategyCard.
 *
 * @param {unknown} raw - Parsed `exit_strategy_reports` value
 * @param {unknown} [strategyS3KeysRaw] - Parsed `exit_strategy_s3_keys` map
 * @param {unknown} [strategyPdfUrlsRaw] - Parsed `exit_strategy_pdf_urls` map (optional)
 * @param {unknown} [strategyPdfS3KeysRaw] - Parsed `exit_strategy_pdf_s3_keys` map (optional)
 * @returns {Array<Object>}
 */
export function exitStrategyReportsToStrategies(
  raw,
  strategyS3KeysRaw = null,
  strategyPdfUrlsRaw = null,
  strategyPdfS3KeysRaw = null
) {
  let reports = raw;
  if (typeof reports === "string") {
    try {
      reports = JSON.parse(reports);
    } catch {
      return [];
    }
  }
  if (!reports || typeof reports !== "object") {
    return [];
  }
  const strategyS3Keys = normalizeStrategyStringMap(strategyS3KeysRaw);
  const strategyPdfUrls = normalizeStrategyStringMap(strategyPdfUrlsRaw);
  const strategyPdfS3Keys = normalizeStrategyStringMap(strategyPdfS3KeysRaw);

  /** @type {Array<[string, unknown]>} */
  let entries;
  if (Array.isArray(reports)) {
    entries = reports.map((row, i) => {
      const obj = coerceExitStrategyReportRow(row);
      const key =
        (obj && (obj.strategy_name ?? obj.slug ?? obj.id)) != null
          ? String(obj.strategy_name ?? obj.slug ?? obj.id)
          : `strategy_${i}`;
      return [key, row];
    });
  } else {
    entries = Object.entries(reports);
  }

  const out = [];
  for (const [strategy_name, row] of entries) {
    const rowObj = coerceExitStrategyReportRow(row);
    if (!rowObj) continue;
    if (rowStatusBlocksHydrate(rowObj.status)) continue;
    if (rowHasMeaningfulError(rowObj.error)) continue;

    let markdown = rowObj.analysis_markdown ?? rowObj.analysis ?? rowObj.report_markdown ?? rowObj.body;
    const mdLeaf = unwrapDynamoLeaf(markdown, 0);
    if (mdLeaf !== undefined && typeof mdLeaf === "string") {
      markdown = mdLeaf;
    }
    if (typeof markdown === "object" && markdown !== null && "S" in markdown) {
      const unwrapped = unwrapStringAttribute(markdown);
      markdown = unwrapped ?? "";
    }
    if (typeof markdown !== "string") markdown = "";
    const markdownTrimmed = markdown.trim();

    const rowPdfUrl = pickExitStrategyReportPdfUrl(rowObj);
    const mapPdfUrlRaw = strategyPdfUrls[strategy_name];
    const mapPdfUrl = typeof mapPdfUrlRaw === "string" ? mapPdfUrlRaw.trim() : "";
    const report_pdf_url =
      rowPdfUrl || (mapPdfUrl && /^https?:\/\//i.test(mapPdfUrl) ? mapPdfUrl : null);
    const rowPdfS3Key = pickExitStrategyReportPdfS3Key(rowObj);
    const mapPdfS3KeyRaw = strategyPdfS3Keys[strategy_name];
    const mapPdfS3Key = typeof mapPdfS3KeyRaw === "string" ? mapPdfS3KeyRaw.trim() : "";
    const pdf_s3_key = rowPdfS3Key || mapPdfS3Key || null;

    if (!markdownTrimmed && !report_pdf_url && !pdf_s3_key) continue;

    const dealPrice = pickDealPriceForStrategy(rowObj, markdownTrimmed);
    const breakEvenPrice = pickBreakEvenPriceForStrategy(rowObj, markdownTrimmed);
    const viableAtAnyPrice = coerceBooleanField(rowObj.viable_at_any_price);
    const profitCurve = coerceProfitCurve(rowObj.profit_curve);

    const displayLeaf = unwrapDynamoLeaf(rowObj.display_name, 0);
    const displayNameFromApi =
      typeof displayLeaf === "string" && displayLeaf.trim() !== ""
        ? displayLeaf.trim()
        : typeof rowObj.display_name === "string" && rowObj.display_name.trim() !== ""
          ? rowObj.display_name.trim()
          : "";
    const displayName = exitStrategyCardTitle(strategy_name, displayNameFromApi);

    const scoreLeaf = unwrapDynamoLeaf(rowObj.score, 0);
    const scoreVal = scoreLeaf !== undefined && scoreLeaf !== null ? scoreLeaf : rowObj.score;
    const s3KeyLeaf = unwrapDynamoLeaf(rowObj.s3_key ?? rowObj.s3Key, 0);
    const s3KeyRaw =
      typeof s3KeyLeaf === "string"
        ? s3KeyLeaf
        : typeof rowObj.s3_key === "string"
          ? rowObj.s3_key
          : typeof rowObj.s3Key === "string"
            ? rowObj.s3Key
            : typeof rowObj.s3_key === "object" && rowObj.s3_key !== null && "S" in rowObj.s3_key
              ? unwrapStringAttribute(rowObj.s3_key)
              : typeof rowObj.s3Key === "object" && rowObj.s3Key !== null && "S" in rowObj.s3Key
                ? unwrapStringAttribute(rowObj.s3Key)
                : null;
    const mapS3KeyRaw = strategyS3Keys[strategy_name];
    const mapS3Key = typeof mapS3KeyRaw === "string" ? mapS3KeyRaw.trim() : "";
    const rowS3Key = typeof s3KeyRaw === "string" ? s3KeyRaw.trim() : "";
    const s3Key = rowS3Key || mapS3Key || null;

    out.push({
      strategy_name,
      display_name: displayName,
      score: scoreVal,
      analysis_markdown: markdownTrimmed,
      break_even_price: breakEvenPrice ?? null,
      viable_at_any_price: viableAtAnyPrice,
      profit_curve: profitCurve,
      true_deal_price: dealPrice ?? null,
      price: dealPrice ?? null,
      s3_key: s3Key,
      report_pdf_url,
      pdf_s3_key,
    });
  }

  if (IS_DEV && out.length > 0) {
    const withPrice = out.filter((s) => s.price != null && s.price !== "").length;
    const sample = out[0];
    const firstRaw = Array.isArray(reports) ? reports[0] : Object.values(reports)[0];
    const rawSample = firstRaw && typeof firstRaw === "object" ? Object.keys(firstRaw).slice(0, 20) : typeof firstRaw;
    logInfo("ExitStrategy", "exitStrategyReportsToStrategies [debug]", {
      total: out.length,
      withPrice,
      sampleStrategyKeys: sample ? Object.keys(sample) : [],
      samplePrice: sample?.price ?? null,
      rawRowKeySample: rawSample,
      markdownHasPriceToken: sample?.analysis_markdown ? /PRICE:/i.test(String(sample.analysis_markdown)) : false,
    });
  }

  return out;
}

/**
 * Strips `property#` for list/batch API variables (matches PropertyDetailsStore).
 *
 * @param {string} propertyId
 * @returns {string}
 */
function toLookupPropertyId(propertyId) {
  const normalized = String(propertyId ?? "").trim();
  if (!normalized) return "";
  return normalized.replace(/^property#/i, "").trim();
}

/**
 * Parses string `additional_attributes` on a property row (batch responses may omit normalizeProperty).
 *
 * @param {Object|null|undefined} property
 * @returns {Object|null}
 */
function ensureParsedAdditionalAttributes(property) {
  if (!property || typeof property !== "object") return null;
  const next = { ...property };
  if (typeof next.additional_attributes === "string") {
    try {
      const parsed = JSON.parse(next.additional_attributes);
      if (parsed && typeof parsed === "object") {
        next.additional_attributes = parsed;
      }
    } catch {
      // leave as string — hydrate will not find exit_strategy_reports
    }
  }
  return next;
}

/**
 * Pulls one property from batchGetProperties body (same resolution rules as PropertyDetailsStore).
 *
 * @param {string} body
 * @param {string} requestedPropertyId
 * @param {string} lookupPropertyId
 * @returns {Object|null}
 */
function extractPropertyFromBatchBody(body, requestedPropertyId, lookupPropertyId) {
  if (typeof body !== "string") return null;
  let bodyString;
  try {
    bodyString = extractBodyString(body);
  } catch {
    return null;
  }
  let parsed;
  try {
    parsed = JSON.parse(bodyString || "{}");
  } catch {
    return null;
  }
  const properties = parsed?.properties;
  if (!properties || typeof properties !== "object") return null;
  if (properties[requestedPropertyId]) return properties[requestedPropertyId];
  if (properties[lookupPropertyId]) return properties[lookupPropertyId];
  const entries = Object.values(properties);
  return entries.find((item) => cachedPropertyIdMatchesKey(item?.id, requestedPropertyId)) || null;
}

/**
 * ExitStrategyStore
 *
 * On start: loads saved results from the property record (`additional_attributes.exit_strategy_reports`)
 * via `fetchProperties` when present; otherwise runs the live agent through ProcessTrackerStore
 * (invoke + subscription handled by the registered process handler).
 *
 * Status transitions: idle → invoking → processing → done | error
 */
class ExitStrategyStore {
  /** @type {'idle'|'invoking'|'processing'|'done'|'error'} */
  status = "idle";

  /** @type {Array<Object>} Incrementally populated strategy result objects */
  strategies = [];

  /** @type {string} Latest progress message from the backend */
  progressMessage = "";

  /** @type {string|null} Terminal error message */
  error = null;

  /** @type {string} Property ID currently being analyzed */
  propertyId = "";

  /** @type {Set<string>} Strategy names currently being retried via live invoke */
  retryingStrategies = new Set();

  /** @type {Map<string, string>} strategy_name → error message for the last failed retry */
  retryErrors = new Map();

  _processTrackerStore = null;

  constructor(processTrackerStore) {
    this._processTrackerStore = processTrackerStore ?? null;
    makeAutoObservable(this, { _processTrackerStore: false }, { autoBind: true });
  }

  /**
   * Loads strategies from `fetchProperties` when `additional_attributes.exit_strategy_reports` has data.
   *
   * @param {string} customer
   * @param {string} propertyId
   * @returns {Promise<boolean>} True when cached strategies were applied
   */
  async _tryHydrateFromProperty(customer, propertyId) {
    const requestedPropertyId = String(propertyId ?? "").trim();
    const lookupPropertyId = toLookupPropertyId(requestedPropertyId);
    if (!lookupPropertyId) {
      logInfo("ExitStrategy", "Hydrate skipped: empty property id", {});
      return false;
    }

    logInfo("ExitStrategy", "Hydrate: fetching property for cached exit strategies", {
      requestedPropertyId,
      lookupPropertyId,
      customer,
    });

    let propertyRecord = null;

    const listResult = await fetchPropertiesFromApi({
      customer,
      propertyIds: [lookupPropertyId],
      limit: 1,
    });

    const firstList =
      listResult?.isEmpty || !Array.isArray(listResult.data) || listResult.data.length === 0
        ? null
        : ensureParsedAdditionalAttributes(listResult.data[0]);

    if (firstList && cachedPropertyIdMatchesKey(firstList.id, requestedPropertyId)) {
      propertyRecord = firstList;
      logInfo("ExitStrategy", "Hydrate: matched row from fetchProperties", {
        id: firstList.id,
      });
    } else if (firstList) {
      logInfo("ExitStrategy", "Hydrate: fetchProperties first row id mismatch", {
        requestedPropertyId,
        rowId: firstList?.id ?? null,
      });
    } else {
      logInfo("ExitStrategy", "Hydrate: fetchProperties returned no rows", {
        lookupPropertyId,
      });
    }

    if (!propertyRecord && requestedPropertyId.startsWith("property#")) {
      try {
        const batchResponse = await graphqlRequest({
          query: batchGetProperties,
          variables: {
            customer_id: customer,
            property_ids: [lookupPropertyId],
          },
          authMode: "AMAZON_COGNITO_USER_POOLS",
        });
        const batchResult = batchResponse?.data?.batchGetProperties;
        if (isSuccessStatusCode(batchResult?.statusCode) && batchResult?.body) {
          const raw = extractPropertyFromBatchBody(batchResult.body, requestedPropertyId, lookupPropertyId);
          if (raw) {
            propertyRecord = ensureParsedAdditionalAttributes(normalizeProperty(raw, 0));
            logInfo("ExitStrategy", "Hydrate: loaded property from batchGetProperties", {
              id: propertyRecord?.id ?? null,
            });
          } else {
            logInfo("ExitStrategy", "Hydrate: batchGetProperties body had no matching property", {
              requestedPropertyId,
            });
          }
        } else {
          logInfo("ExitStrategy", "Hydrate: batchGetProperties non-success", {
            statusCode: batchResult?.statusCode ?? null,
          });
        }
      } catch (err) {
        logError("ExitStrategy", err, { phase: "hydrate-batch", requestedPropertyId });
      }
    }

    if (!propertyRecord && firstList && !requestedPropertyId.startsWith("property#")) {
      propertyRecord = firstList;
      logInfo("ExitStrategy", "Hydrate: using fetchProperties row (non property# id)", {
        id: firstList.id,
      });
    }

    if (!propertyRecord) {
      logInfo("ExitStrategy", "Hydrate: no property record to read", { requestedPropertyId });
      return false;
    }

    const attrs = propertyRecord.additional_attributes;
    const reports = attrs?.exit_strategy_reports;
    if (reports == null) {
      logInfo("ExitStrategy", "Hydrate: additional_attributes has no exit_strategy_reports", {
        hasAttrs: Boolean(attrs),
        attrKeySample: attrs && typeof attrs === "object" && !Array.isArray(attrs) ? Object.keys(attrs).slice(0, 25) : [],
      });
      return false;
    }

    const strategies = exitStrategyReportsToStrategies(
      reports,
      attrs?.exit_strategy_s3_keys,
      attrs?.exit_strategy_pdf_urls,
      attrs?.exit_strategy_pdf_s3_keys
    );
    if (strategies.length === 0) {
      logInfo("ExitStrategy", "Hydrate: exit_strategy_reports empty after normalization", {
        reportKeyCount: typeof reports === "object" && reports && !Array.isArray(reports) ? Object.keys(reports).length : 0,
      });
      return false;
    }

    runInAction(() => {
      this.strategies = strategies;
    });
    logInfo("ExitStrategy", "Hydrated from property — skipping live agent", {
      propertyId: requestedPropertyId,
      strategyCount: strategies.length,
    });
    return true;
  }

  /**
   * Invokes the live exit strategy agent via ProcessTrackerStore.
   * Caller must set `invoking` / clear state first.
   *
   * @param {Object} params
   * @param {string} params.propertyId
   * @param {string} params.userId
   * @param {string} params.customer
   * @returns {Promise<void>}
   */
  async _invokeLive({ propertyId, userId, customer, strategies, modelName, isPartialRetry = false }) {
    let safetyTimer = null;
    try {
      runInAction(() => {
        this.status = "processing";
      });

      if (!this._processTrackerStore) {
        throw new Error("ProcessTrackerStore is not available");
      }

      const hasStrategiesFilter = Array.isArray(strategies) && strategies.length > 0;
      const RETRY_SAFETY_TIMEOUT_MS = 5 * 60 * 1000;
      if (isPartialRetry) {
        safetyTimer = setTimeout(() => {
          runInAction(() => { this.retryingStrategies.clear(); });
        }, RETRY_SAFETY_TIMEOUT_MS);
      }

      await this._processTrackerStore.startProcess(PROCESS_TYPE.EXIT_STRATEGY, {
        propertyId,
        params: {
          propertyId,
          userId,
          customerId: customer,
          ...(hasStrategiesFilter ? { strategies } : {}),
          ...(modelName ? { modelName } : {}),
        },
        notifyOnError: false,
        onComplete: ({ payload }) => {
          const incoming = Array.isArray(payload?.strategies)
            ? payload.strategies.map(withExitStrategyDisplayTitle)
            : [];
          runInAction(() => {
            if (isPartialRetry) {
              const current = Array.isArray(this.strategies) ? [...this.strategies] : [];
              for (const item of incoming) {
                const key = normalizeStrategyNameForRetry(item?.strategy_name);
                if (!key) {
                  current.push(item);
                  continue;
                }
                const idx = current.findIndex(
                  (existing) => normalizeStrategyNameForRetry(existing?.strategy_name) === key
                );
                if (idx >= 0) current[idx] = item;
                else current.push(item);
              }
              this.strategies = current;
              this.retryingStrategies.clear();
            } else {
              this.strategies = incoming;
            }
            this.status = "done";
          });
        },
      });
    } catch (err) {
      logError("ExitStrategy", err, { propertyId });
      runInAction(() => {
        this.status = "error";
        this.error = "Failed to start exit strategy analysis. Please try again.";
        this.retryingStrategies.clear();
      });
    } finally {
      if (safetyTimer) clearTimeout(safetyTimer);
    }
  }

  /**
   * Entry point: hydrate from saved property data when available; otherwise run the agent.
   *
   * @param {Object} params
   * @param {string} params.propertyId
   * @param {string} params.userId
   * @param {string} params.customer
   * @param {boolean} [params.forceLive] - Skip cache (e.g. Try Again after a completed run)
   * @param {Array<string>} [params.strategies] - Strategy keys to execute. Empty arrays are rejected.
   * @param {string} [params.modelName] - Superadmin-selected model override forwarded to the Lambda.
   * @returns {Promise<void>}
   */
  async startAnalysis({ propertyId, userId, customer, forceLive = false, strategies, modelName } = {}) {
    const hasStrategiesParam = Array.isArray(strategies);
    const isPartialRetry = forceLive && hasStrategiesParam && strategies.length > 0;
    if (hasStrategiesParam && strategies.length === 0) {
      runInAction(() => {
        this.status = "error";
        this.error = "Select at least one exit strategy.";
      });
      return;
    }

    runInAction(() => {
      this.status = "invoking";
      this.propertyId = propertyId;
      if (!isPartialRetry) {
        this.strategies = [];
      } else {
        for (const key of strategies) {
          const norm = normalizeStrategyNameForRetry(key);
          if (norm) this.retryingStrategies.add(norm);
        }
      }
      this.progressMessage = forceLive ? "" : "Loading saved analysis…";
      this.error = null;
    });

    if (!forceLive) {
      try {
        const hydrated = await this._tryHydrateFromProperty(customer, propertyId);
        if (hydrated) {
          runInAction(() => {
            this.status = "done";
            this.progressMessage = "";
          });
          return;
        }
      } catch (err) {
        logError("ExitStrategy", err, { propertyId, phase: "hydrate" });
        // Fall through to live agent
      }
      logInfo("ExitStrategy", "No cached strategies — invoking live exit strategy agent", {
        propertyId,
      });
    }

    runInAction(() => {
      this.progressMessage = "";
    });

    await this._invokeLive({ propertyId, userId, customer, strategies, modelName, isPartialRetry });
  }

  /**
   * Re-runs a single strategy without replacing the full result set.
   *
   * @param {string} strategyName
   * @param {Object} params
   * @param {string} params.propertyId
   * @param {string} params.userId
   * @param {string} params.customer
   * @param {string} [params.modelName] - Superadmin-selected model override forwarded to the Lambda.
   * @returns {Promise<void>}
   */
  async retryStrategy(strategyName, { propertyId, userId, customer, modelName } = {}) {
    const normalizedStrategy = normalizeStrategyNameForRetry(strategyName);
    const resolvedPropertyId = propertyId || this.propertyId;
    if (!normalizedStrategy || !resolvedPropertyId || !userId || !customer) return;
    if (this.retryingStrategies.has(normalizedStrategy)) return;

    runInAction(() => {
      this.retryingStrategies.add(normalizedStrategy);
      this.retryErrors.delete(normalizedStrategy);
    });

    try {
      const SINGLE_RETRY_TIMEOUT_MS = 3 * 60 * 1000;
      const processPromise = runExitStrategyProcess(
        {
          propertyId: resolvedPropertyId,
          userId,
          customerId: customer,
          strategies: [normalizedStrategy],
          ...(modelName ? { modelName } : {}),
        },
        {
          setProgress: () => {},
          setCorrelationId: () => {},
          registerTeardown: () => {},
        }
      );
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Strategy retry timed out")), SINGLE_RETRY_TIMEOUT_MS)
      );
      const output = await Promise.race([processPromise, timeoutPromise]);

      const retriedStrategies = Array.isArray(output?.payload?.strategies) ? output.payload.strategies : [];
      const replacement =
        retriedStrategies.find(
          (item) => normalizeStrategyNameForRetry(item?.strategy_name) === normalizedStrategy
        ) ?? retriedStrategies[0];
      if (!replacement || typeof replacement !== "object") return;

      runInAction(() => {
        const current = Array.isArray(this.strategies) ? [...this.strategies] : [];
        const existingIndex = current.findIndex(
          (item) => normalizeStrategyNameForRetry(item?.strategy_name) === normalizedStrategy
        );
        if (existingIndex >= 0) {
          current[existingIndex] = withExitStrategyDisplayTitle(replacement);
        } else {
          current.push(withExitStrategyDisplayTitle(replacement));
        }
        this.strategies = current;
      });
    } catch (err) {
      logError("ExitStrategy", err, {
        phase: "retry-single-strategy",
        strategy: normalizedStrategy,
        propertyId: resolvedPropertyId,
      });
      runInAction(() => {
        this.retryErrors.set(normalizedStrategy, err?.message ?? "Retry failed");
      });
    } finally {
      runInAction(() => {
        this.retryingStrategies.delete(normalizedStrategy);
      });
    }
  }

  /**
   * @param {string} strategyName
   * @returns {boolean}
   */
  isRetrying(strategyName) {
    const normalizedStrategy = normalizeStrategyNameForRetry(strategyName);
    if (!normalizedStrategy) return false;
    return this.retryingStrategies.has(normalizedStrategy);
  }

  /**
   * @param {string} strategyName
   * @returns {string|null}
   */
  getRetryError(strategyName) {
    const normalizedStrategy = normalizeStrategyNameForRetry(strategyName);
    if (!normalizedStrategy) return null;
    return this.retryErrors.get(normalizedStrategy) ?? null;
  }

  reset() {
    this.status = "idle";
    this.strategies = [];
    this.progressMessage = "";
    this.error = null;
    this.propertyId = "";
    this.retryingStrategies.clear();
    this.retryErrors.clear();
  }

  resetForNewCustomer() {
    this.reset();
  }
}

export default ExitStrategyStore;
